import { z } from "zod";

export const TrackingType = z.enum(["injection", "surgery", "medication", "product", "shockwave", "education"]);

export type TrackingType = z.infer<typeof TrackingType>;

const emptyOptionalValueToUndefined = (value: unknown) =>
    value == null || value === "" || value === "null" || value === "undefined" ? undefined : value;

/** Optional UUID fields may arrive as empty strings from URL search params or saved templates. */
const optionalUuidZ = z.preprocess(emptyOptionalValueToUndefined, z.string().uuid().optional());

export const QuickstartTrackerT = z.object({
    form_id: z.string(),
    /** Main schedule (from procedure / reference date). Optional when only a back-up schedule is set. */
    schedule_id: optionalUuidZ,
    /** Alternate schedule when the procedure date is set after diary creation. */
    back_up_schedule_id: optionalUuidZ,
    is_hidden: z.boolean().optional(),
    /** When true, patients/clinicians can add unscheduled extra scores for this tracker. */
    allow_extra_scores: z.boolean().optional(),
    /** 0 means unordered. Positive values define the ordered unlock sequence. */
    sort_order: z.coerce.number().int().min(0).optional(),
    roles: z.array(z.string()).nullable().optional(),
});

export type QuickstartTrackerT = z.infer<typeof QuickstartTrackerT>;

export const QuickstartInjectionDetailT = z.object({
    side: z.string().nullable(),
    orthopaedic_area: z.string().nullable(),
    site: z.string().nullable(),
    injection_subsite: z.string().nullable(),
    injection_substance: z.string().nullable(),
    dose: z.string().nullable(),
    unit: z.string().nullable(),
});

export type QuickstartInjectionDetailT = z.infer<typeof QuickstartInjectionDetailT>;

export const QuickstartShockwaveDetailT = z.object({
    side: z.string().nullable(),
    orthopaedic_area: z.string().nullable(),
    site: z.string().nullable(),
    subsite: z.string().nullable(),
    shockwave_type: z.string().nullable(),
    dose: z.string().nullable(),
    unit: z.string().nullable(),
    impulses: z.string().nullable(),
});

export type QuickstartShockwaveDetailT = z.infer<typeof QuickstartShockwaveDetailT>;

export interface QuickstartInjectionEventT {
    type: "injection";
    referring_clinician?: string;
    injection_clinician?: string;
    injection_method?: string;
    side?: string;
    anatomic_area?: string;
    site?: string;
    injection_subsite?: string;
    substance?: string;
    dose?: string;
    unit?: string;
}

export interface QuickstartSurgeryEventT {
    type: "surgery";
    referring_clinician?: string;
    surgery_type?: string;
    surgery_details?: string;
    side?: string;
    anatomic_area?: string;
}

export interface QuickstartMedicationEventT {
    type: "medication";
    medication_name?: string;
    dose?: string;
    unit?: string;
    frequency?: string;
    duration?: string;
}

export interface QuickstartProductEventT {
    type: "product";
    product_name?: string;
    product_type?: string;
    usage_instructions?: string;
}

export interface QuickstartShockwaveEventT {
    type: "shockwave";
    referring_clinician?: string;
    treatment_area?: string;
    intensity?: string;
    duration?: string;
}

export type QuickstartEventT =
    | QuickstartInjectionEventT
    | QuickstartSurgeryEventT
    | QuickstartMedicationEventT
    | QuickstartProductEventT
    | QuickstartShockwaveEventT;

export const ContactSchema = z.object({
    contact_id: z.string().uuid("Contact person is required"),
});

const injectionDetailSchema = z.object({
    injection_detail_id: optionalUuidZ,
    injection_id: optionalUuidZ,
    side: z.string().nullable().optional(),
    orthopaedic_area: z.string().nullable().optional(),
    site: z.string().nullable().optional(),
    subsite: z.string().nullable().optional(),
    injection_substance: z.string().nullable().optional(),
    dose: z.string().nullable().optional(),
    unit: z.string().nullable().optional(),
});

const shockwaveDetailSchema = z.object({
    shockwave_detail_id: optionalUuidZ,
    shockwave_id: optionalUuidZ,
    side: z.string().nullable().optional(),
    orthopaedic_area: z.string().nullable().optional(),
    site: z.string().nullable().optional(),
    subsite: z.string().nullable().optional(),
    shockwave_type: z.enum(["radial", "focused"]).nullable().optional(),
    dose: z.string().nullable().optional(),
    unit: z.string().optional(),
    impulses: z.string().nullable().optional(),
});

/** Roles that may sign a consent on the diary (mirrors tracker roles). */
export const templateConsentRoleOptions = [
    { value: "patient", label: "Patient" },
    { value: "clinician", label: "Clinician" },
    { value: "secretary", label: "Secretary" },
] as const;

/** A consent body selected on a template, to be created on the patient's diary. */
export const consentSelectionSchema = z.object({
    consent_body_id: z.string().uuid(),
    /** Who must sign this consent. Defaults to patient-only when omitted. */
    roles: z.array(z.string()).nullable().optional(),
});

export type ConsentSelectionT = z.infer<typeof consentSelectionSchema>;

export const templateSettingsSchema = z.object({
    // Quickstart
    quickstart_key: z.string().optional(),

    // Admin Form
    organisation_id: z.string().optional().nullable(),
    organisation_name: z.string().optional().nullable(),
    /** Hospital number (e.g. NHS number) collected from clinician when organisation is NHS. */
    hospital_number: z.string().optional().nullable(),
    /**
     * Whether diaries created from this template require a hospital number.
     * `null` / omitted = default from the diary organisation `nhs_flag` at create time.
     * `true` = always collect; `false` = never collect (skip setup and invite field).
     */
    require_hospital_number: z.boolean().nullable().optional(),
    contact_id: z.string().uuid().nullable().optional(),
    name: z.string().optional(),
    description: z.string().optional(),
    invited_by: z.string().optional(),
    invited_by_formal_name: z.string().optional(),

    // Trackers Form
    trackers: z.array(QuickstartTrackerT),

    // Consents Form — reusable consent bodies (public.consent_bodies) shown to
    // the patient on the diary. Array order defines signing/display order.
    consents: z.array(consentSelectionSchema).optional(),

    // Events Form
    required_events: z.array(TrackingType),

    // Injection Form
    injection_id: z.string().nullable().optional(),
    referring_clinician_id: z.string().nullable().optional(),
    injecting_clinician_id: z.string().nullable().optional(),
    injection_method_id: z.string().nullable().optional(),
    injection_details: z.array(injectionDetailSchema).optional(),

    // Surgery Form
    surgery_id: z.string().nullable().optional(),
    operating_clinician_id: z.string().nullable().optional(),
    side: z.string().nullable().optional(),
    orthopaedic_area: z.string().nullable().optional(),
    surgery_option_id: z.string().nullable().optional(),

    // Education Form
    education_id: z.string().nullable().optional(),
    education_name: z.string().nullable().optional(),
    education_description: z.string().nullable().optional(),
    educated_by: z.string().nullable().optional(),

    // Shockwave Form
    shockwave_id: z.string().nullable().optional(),
    shockwaving_clinician_id: z.string().nullable().optional(),
    shockwave_details: z.array(shockwaveDetailSchema).optional(),

    // Product Form
    product_id: z.string().nullable().optional(),
    product_option_id: z.string().nullable().optional(),
    provided_by: z.string().nullable().optional(),
    usage_instructions: z.string().nullable().optional(),

    /** BCP-47 programme language (e.g. en-GB, de-DE). Overrides user preference for forms and reminders. */
    locale: z.string().optional(),

    /** Labels for grouping diaries (e.g. study names). Set on templates; applied to diaries when created via quickstart. */
    diary_labels: z.array(z.string()).optional(),
});

export type TemplateSettingsSchemaT = z.infer<typeof templateSettingsSchema>;

export type QuickstartDataSchemaT = TemplateSettingsSchemaT & {
    patient_id: string;
};

export interface QuickstartSettingsStepT {
    stage: "past" | "current" | "future";
    words: string;
}
